/**
 * Minimal accessible tour layer (no third-party tour library).
 * - Anchors are stable `data-tour="..."` attributes only (never CSS classes or DOM position).
 * - Built on Radix Dialog: focus trap, Escape to leave, focus restored afterwards.
 * - If the anchor is missing/hidden (e.g. responsive layout), the step is shown centered.
 * - Repositions on resize/scroll; motion respects reduced-motion tokens.
 */
import { Dialog as D } from "radix-ui";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import type { Tour, TourStep } from "@/api/types";
import { Button } from "@/components/ui";

const GAP = 12;
const CARD_WIDTH = 320;

type Rect = { top: number; left: number; width: number; height: number };

function findAnchor(anchor: string): HTMLElement | null {
  if (!anchor) return null;
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(anchor)}"]`));
  return candidates.find((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden") ?? null;
}

function placeCard(rect: Rect | null, placement: string, cardHeight: number): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_WIDTH, vw - 24);
  if (!rect || vw < 640) return { top: Math.max(12, (vh - cardHeight) / 2), left: (vw - width) / 2 };
  let top = rect.top + rect.height + GAP;
  let left = rect.left;
  if (placement === "right") {
    top = rect.top;
    left = rect.left + rect.width + GAP;
  } else if (placement === "top") {
    top = rect.top - cardHeight - GAP;
  } else if (placement === "left") {
    top = rect.top;
    left = rect.left - width - GAP;
  }
  if (top + cardHeight > vh - 12) top = Math.max(12, rect.top - cardHeight - GAP);
  if (top < 12) top = Math.min(vh - cardHeight - 12, rect.top + rect.height + GAP);
  return { top: Math.max(12, top), left: Math.min(Math.max(12, left), vw - width - 12) };
}

export function TourOverlay({
  tour,
  steps,
  index,
  onIndexChange,
  onFinish,
  onExit,
}: {
  tour: Tour;
  steps: TourStep[];
  index: number;
  onIndexChange: (index: number) => void;
  onFinish: () => void;
  onExit: () => void;
}) {
  const step = steps[index];
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardHeight, setCardHeight] = useState(180);
  const cardRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const frame = useRef(0);

  const measure = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = findAnchor(step.anchor);
      if (!el) return setRect(null);
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
    });
  }, [step.anchor]);

  useLayoutEffect(() => {
    const el = findAnchor(step.anchor);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    measure();
    const observer = new ResizeObserver(measure);
    if (el) observer.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      cancelAnimationFrame(frame.current);
    };
  }, [step.anchor, measure]);

  useLayoutEffect(() => {
    titleRef.current?.focus();
  }, [index]);

  const position = placeCard(rect, step.placement, cardHeight);
  const last = index === steps.length - 1;
  const pad = 4;

  return (
    <D.Root open onOpenChange={(open) => !open && onExit()}>
      <D.Portal>
        {rect ? (
          <div
            aria-hidden
            className="pointer-events-none fixed z-[55] rounded-md transition-all transition-base"
            style={{
              top: rect.top - pad,
              left: rect.left - pad,
              width: rect.width + pad * 2,
              height: rect.height + pad * 2,
              boxShadow: "0 0 0 2px var(--accent-indicator), 0 0 0 9999px var(--overlay)",
            }}
          />
        ) : (
          <div aria-hidden className="animate-fade-in fixed inset-0 z-[55] bg-overlay" />
        )}
        <D.Content
          ref={cardRef}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            titleRef.current?.focus();
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="animate-pop-in fixed z-[60] rounded-lg border border-line bg-surface-raised p-4 shadow-lg"
          style={{ top: position.top, left: position.left, width: Math.min(CARD_WIDTH, window.innerWidth - 24) }}
        >
          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-fg-subtle">
            <span>{tour.title}</span>
            <span className="tabular" aria-label={`Step ${index + 1} of ${steps.length}`}>
              {index + 1} / {steps.length}
            </span>
          </div>
          <D.Title asChild>
            <h2 ref={titleRef} tabIndex={-1} className="text-base font-semibold text-fg outline-none focus-visible:shadow-none">
              {step.title}
            </h2>
          </D.Title>
          <D.Description className="mt-1 text-sm text-fg-muted">{step.body}</D.Description>
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={onExit}>
              {last ? "Close" : "Skip tour"}
            </Button>
            <div className="flex gap-2">
              {index > 0 && (
                <Button size="sm" onClick={() => onIndexChange(index - 1)}>
                  Back
                </Button>
              )}
              <Button size="sm" variant="primary" onClick={() => (last ? onFinish() : onIndexChange(index + 1))}>
                {last ? "Done" : "Next"}
              </Button>
            </div>
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
